import type {PostHog} from 'posthog-js';

export interface PostHogEventProperties {
  [key: string]: any;
}

export interface PostHogUserProperties {
  email?: string;
  name?: string;
  plan?: string;
  company?: string;
  [key: string]: any;
}

class PostHogAnalytics {
  private posthog: PostHog | null = null;
  private initialized = false;

  private initializePostHog(): void {
    try {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.debug('PostHog not available on localhost');
        return;
      }

      const nuxtApp = useNuxtApp();
      if (nuxtApp.$posthog && typeof nuxtApp.$posthog === 'function') {
        this.posthog = nuxtApp.$posthog();
      }
    } catch (error) {
      console.warn('PostHog not available:', error);
    }
  }

  private isAvailable(): boolean {
    if (!this.initialized && import.meta.client) {
      this.initializePostHog();
    }
    return import.meta.client && this.posthog !== null;
  }

  capture(eventName: string, properties?: PostHogEventProperties): void {
    if (!this.isAvailable()) return;

    try {
      this.posthog!.capture(eventName, {
        timestamp: new Date(),
        ...properties,
      });
    } catch (error) {
      console.error('Failed to capture PostHog event:', error);
    }
  }

  identify(userId: string, userProperties?: PostHogUserProperties): void {
    if (!this.isAvailable()) return;

    try {
      this.posthog!.identify(userId, userProperties);
    } catch (error) {
      console.error('Failed to identify user in PostHog:', error);
    }
  }

  setPersonProperties(properties: PostHogUserProperties): void {
    if (!this.isAvailable()) return;

    try {
      this.posthog!.setPersonProperties(properties);
    } catch (error) {
      console.error('Failed to set person properties in PostHog:', error);
    }
  }

  reset(): void {
    if (!this.isAvailable()) return;

    try {
      this.posthog!.reset();
    } catch (error) {
      console.error('Failed to reset PostHog:', error);
    }
  }

  getFeatureFlag(flagKey: string): string | boolean | undefined {
    if (!this.isAvailable()) return undefined;

    try {
      return this.posthog!.getFeatureFlag(flagKey);
    } catch (error) {
      console.error('Failed to get feature flag from PostHog:', error);
      return undefined;
    }
  }

  isFeatureEnabled(flagKey: string): boolean {
    if (!this.isAvailable()) return false;

    try {
      return this.posthog!.isFeatureEnabled(flagKey) || false;
    } catch (error) {
      console.error('Failed to check feature flag in PostHog:', error);
      return false;
    }
  }

  capturePageView(path?: string): void {
    if (!this.isAvailable()) return;

    try {
      this.posthog!.capture('$pageview', {
        $current_url: path || (typeof window !== 'undefined' ? window.location.href : ''),
      });
    } catch (error) {
      console.error('Failed to capture page view in PostHog:', error);
    }
  }

  getInstance(): PostHog | null {
    return this.posthog;
  }
}

export const analytics = new PostHogAnalytics();

export const trackEvent = (eventName: string, properties?: PostHogEventProperties) => {
  analytics.capture(eventName, properties);
};

export const trackUser = (userId: string, userProperties?: PostHogUserProperties) => {
  analytics.identify(userId, userProperties);
};

export const trackPageView = (path?: string) => {
  analytics.capturePageView(path);
};

export const resetUser = () => {
  analytics.reset();
};

export const trackUserLogin = (userId: string, properties?: PostHogEventProperties) => {
  analytics.capture('user_logged_in', {
    user_id: userId,
    ...properties,
  });
};

export const trackPlanUpgrade = (
  fromPlan: string,
  toPlan: string,
  properties?: PostHogEventProperties
) => {
  analytics.capture('plan_upgraded', {
    from_plan: fromPlan,
    to_plan: toPlan,
    ...properties,
  });
};

export const trackFeatureUsed = (featureName: string, properties?: PostHogEventProperties) => {
  analytics.capture('feature_used', {
    feature_name: featureName,
    ...properties,
  });
};

export const trackIntegrationConnected = (
  integration: string,
  properties?: PostHogEventProperties
) => {
  analytics.capture('integration_connected', {
    integration_name: integration,
    ...properties,
  });
};

export const trackSegmentCreated = (segmentType: string, properties?: PostHogEventProperties) => {
  analytics.capture('segment_created', {
    segment_type: segmentType,
    ...properties,
  });
};

export const setConnectionAttempt = (integrationSlug: string) => {
  if (import.meta.client) {
    localStorage.setItem(`vector_recent_connection_${integrationSlug}`, 'true');
  }
};

export const checkAndTrackRecentConnection = (
  integrationSlug: string,
  integrationName: string,
  wasConnected: boolean,
  isConnected: boolean,
  additionalProperties: PostHogEventProperties = {}
) => {
  if (!import.meta.client) return false;

  const recentConnection = localStorage.getItem(`vector_recent_connection_${integrationSlug}`);

  if (recentConnection && !wasConnected && isConnected) {
    trackIntegrationConnected(integrationSlug, {
      integration_name: integrationName,
      ...additionalProperties,
    });

    localStorage.removeItem(`vector_recent_connection_${integrationSlug}`);
    return true;
  }

  return false;
};

export const handleIntegrationConnection = {
  setAttempt: setConnectionAttempt,
  checkAndTrack: checkAndTrackRecentConnection,
};

export default analytics;
